import { useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import { ServiceStatisticsGranularity, WaitRankingRowDTO } from '../../dto/ServiceStatisticsDTO';
import { ALERT_MIN_ATTENTIONS } from './alertThresholds';
import { withoutPreviousComparisons } from './periodDetail';
import { SectionStatus } from './usePeriodDetail';
import { formatMinutes } from './statisticsUtils';
import WaitCauseList from './WaitCauseList';
import { LEVEL_LABEL_ID, LEVEL_TAG_CLASS, RANKING_LEVEL } from './waitLevelText';

interface PeriodDetailCausesProps {
    /** Las filas que vuelve el ranking del período, ya acotadas a la agenda elegida si hay una. */
    rows: WaitRankingRowDTO[];
    status: SectionStatus;
    granularity: ServiceStatisticsGranularity;
    /** Se mira un solo servicio: su nombre ya está en el título del panel. */
    single?: boolean;
    onRetry: () => void;
}

/**
 * «Posibles causas»: las filas del ranking de espera del período, sin las causas que comparan contra el período anterior
 * (en un día, «el anterior» es el día de antes y no dice nada). Sin ninguna fila, dice por qué: el ranking solo trae los
 * servicios con 30 atenciones o más. Si la consulta falla, avisa acá y el resto del panel sigue.
 */
export default function PeriodDetailCauses({ rows, status, granularity, single = false, onRetry }: PeriodDetailCausesProps) {
    const intl = useIntl();

    if (status === 'loading') {
        return (
            <span className="nv-loading ss-detail__loading" role="status">
                <ProgressSpinner strokeWidth="4" style={{ width: 16, height: 16 }} />
                <span>{intl.formatMessage({ id: 'ServicesStatisticsLoading' })}</span>
            </span>
        );
    }
    if (status === 'failed') {
        return (
            <div className="ss-detail__failed" role="status">
                <p className="nv-note">
                    <i className="pi pi-info-circle" aria-hidden="true" />
                    {intl.formatMessage({ id: 'ServicesStatisticsRankingFailed' })}
                </p>
                <Button type="button" link label={intl.formatMessage({ id: 'ServicesStatisticsRetry' })} onClick={onRetry} />
            </div>
        );
    }
    if (rows.length === 0) {
        return (
            <p className="nv-note">
                <i className="pi pi-info-circle" aria-hidden="true" />
                {intl.formatMessage({ id: single ? 'ServicesStatisticsPeriodDetailCausesEmptyService' : 'ServicesStatisticsPeriodDetailCausesEmpty' },
                    { min: ALERT_MIN_ATTENTIONS, unit: granularity })}
            </p>
        );
    }
    return (
        <ul className="ss-detail__causes">
            {rows.map(row => {
                const level = RANKING_LEVEL[row.level];
                return (
                    <li key={row.serviceId} className="ss-detail__cause">
                        <div className="ss-detail__cause-head">
                            {!single && <span className="ss-detail__name">{row.serviceName}</span>}
                            <span className="ss-detail__cause-wait">
                                <span className="ss-detail__minutes">{formatMinutes(row.avgWaitMinutes)}</span>
                                <Tag className={LEVEL_TAG_CLASS[level]} value={intl.formatMessage({ id: LEVEL_LABEL_ID[level] })} />
                            </span>
                        </div>
                        <WaitCauseList causes={withoutPreviousComparisons(row.causes)} level={row.level} />
                    </li>
                );
            })}
        </ul>
    );
}
